const DEFAULT_PRIMARY = "#324D54";
const DEFAULT_SECONDARY = "#c9a227";

const expandHex = (value) => {
  const match = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i.exec(
    String(value || "").trim(),
  );
  if (!match) return null;
  const raw = match[1];
  return raw.length === 3
    ? raw
        .split("")
        .map((character) => character + character)
        .join("")
    : raw;
};

export const normalizeBrandColor = (value, fallback) => {
  const raw = expandHex(value);
  if (!raw) return fallback;
  return `#${raw.toUpperCase()}`;
};

export const readableTextOn = (value) => {
  const raw = expandHex(value);
  if (!raw) return "#ffffff";
  const channel = (index) => parseInt(raw.slice(index, index + 2), 16) / 255;
  const linear = (colour) =>
    colour <= 0.03928
      ? colour / 12.92
      : Math.pow((colour + 0.055) / 1.055, 2.4);
  const luminance =
    0.2126 * linear(channel(0)) +
    0.7152 * linear(channel(2)) +
    0.0722 * linear(channel(4));
  // Choose the higher-contrast foreground. Black overtakes white at an sRGB
  // relative luminance of ~0.179; using a visual-brightness threshold makes
  // saturated colours such as Hisense cyan (#26BEC9) incorrectly receive
  // low-contrast white text.
  return luminance > 0.179 ? "#0f172a" : "#ffffff";
};

const mixWithBlack = (value, amount = 0.14) => {
  const raw = expandHex(value);
  if (!raw) return value;
  const channel = (index) =>
    Math.round(parseInt(raw.slice(index, index + 2), 16) * (1 - amount));
  return `#${[0, 2, 4]
    .map((index) => channel(index).toString(16).padStart(2, "0"))
    .join("")}`;
};

export const colorWithAlpha = (value, alpha) => {
  const raw = expandHex(value);
  if (!raw) return `rgba(50, 77, 84, ${alpha})`;
  const channels = [0, 2, 4].map((index) =>
    parseInt(raw.slice(index, index + 2), 16),
  );
  return `rgba(${channels.join(", ")}, ${alpha})`;
};

export const getBrandTheme = (brand) => {
  // A default parameter only covers `undefined`. BrandProductPage initialises
  // its fetched brand to `null` and calls this on the first render, so every
  // cold load of a brand product URL (QR codes, shared links, banner links)
  // threw `Cannot read properties of null (reading 'primaryColor')` and hit
  // the error boundary. Null is a normal input here: it means "not loaded
  // yet", and the answer is the default theme.
  const source = brand || {};
  const primary = normalizeBrandColor(source.primaryColor, DEFAULT_PRIMARY);
  const secondary = normalizeBrandColor(
    source.secondaryColor,
    DEFAULT_SECONDARY,
  );
  return {
    primary,
    secondary,
    onPrimary: readableTextOn(primary),
    onSecondary: readableTextOn(secondary),
    primaryHover: mixWithBlack(primary),
    secondaryHover: mixWithBlack(secondary),
    secondarySoft: colorWithAlpha(secondary, 0.14),
  };
};

export const getBrandThemeCssVars = (brand) => {
  const theme = getBrandTheme(brand);
  return {
    "--brand-primary": theme.primary,
    "--brand-secondary": theme.secondary,
    "--brand-on-primary": theme.onPrimary,
    "--brand-on-secondary": theme.onSecondary,
    "--brand-primary-hover": theme.primaryHover,
    "--brand-secondary-hover": theme.secondaryHover,
  };
};
